import { useCallback, useEffect, useState } from 'react';

/**
 * Light / dark theme. Default follows the computer's setting; choosing one
 * in the app overrides it and is remembered in this browser. The initial
 * value is applied by an inline script in index.html before first paint so
 * the page never flashes the wrong theme.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'wgp-theme';

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function storedTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function apply(theme: Theme, animate: boolean) {
  const root = document.documentElement;
  if (animate) {
    root.classList.add('theme-switching');
    window.setTimeout(() => root.classList.remove('theme-switching'), 250);
  }
  root.setAttribute('data-theme', theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'light' ? '#F4F4F6' : '#111112');
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => storedTheme() ?? systemTheme());

  // Follow the computer's setting live, unless the person has chosen one.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return;
    const onChange = () => {
      if (!storedTheme()) {
        const next = systemTheme();
        setTheme(next);
        apply(next, true);
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((cur) => {
      const next: Theme = cur === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private mode etc. — the switch still works for this visit.
      }
      apply(next, true);
      return next;
    });
  }, []);

  return { theme, toggle };
}
